import { createServerStateStore } from "./serverState";

export type PlanSlot = "Sabah" | "Öğle" | "Akşam";

export interface SlaughterPlanRecord {
  id?: string;
  animalId: number;
  date?: string;
  planDate?: string;
  slot: PlanSlot;
  time?: string;
  note?: string;
  [key: string]: any;
}

export const PLAN_SLOTS: PlanSlot[] = ["Sabah", "Öğle", "Akşam"];

const DEFAULT_SLOT_TIMES: Record<PlanSlot, string> = {
  Sabah: "09:00",
  Öğle: "12:00",
  Akşam: "18:00",
};

function normalizeSlot(value?: string): PlanSlot {
  const raw = String(value ?? "").trim().toLocaleLowerCase("tr-TR");

  if (raw === "öğle" || raw === "ogle") return "Öğle";
  if (raw === "akşam" || raw === "aksam") return "Akşam";

  return "Sabah";
}

function normalizeRecord(item: any): SlaughterPlanRecord {
  const slot = normalizeSlot(item?.slot);

  return {
    ...item,
    animalId: Number(item?.animalId ?? 0),
    date: item?.date ?? item?.planDate ?? "",
    planDate: item?.planDate ?? item?.date ?? "",
    slot,
    time: item?.time ?? DEFAULT_SLOT_TIMES[slot],
    note: item?.note ?? "",
  };
}

function normalizeRecords(value: unknown): SlaughterPlanRecord[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => normalizeRecord(item))
    .filter((item) => Number.isFinite(item.animalId) && item.animalId > 0);
}

const store = createServerStateStore<SlaughterPlanRecord[]>(
  "slaughter_plan",
  [],
  normalizeRecords
);

export function defaultTimeForSlot(slot?: string) {
  return DEFAULT_SLOT_TIMES[normalizeSlot(slot)];
}

export function getSlaughterPlan() {
  return store.getState();
}

export function getSlaughterPlans() {
  return store.getState();
}

export function getSlaughterPlanItems() {
  return store.getState();
}

export function getSlaughterPlanRecords() {
  return store.getState();
}

export async function rehydrateSlaughterPlan() {
  return store.rehydrate();
}

export async function rehydrateSlaughterPlans() {
  return store.rehydrate();
}

export async function rehydrateSlaughterPlanRecords() {
  return store.rehydrate();
}

export async function setAllSlaughterPlan(
  updater:
    | SlaughterPlanRecord[]
    | ((prev: SlaughterPlanRecord[]) => SlaughterPlanRecord[])
) {
  return store.setState((prev) =>
    normalizeRecords(typeof updater === "function" ? updater(prev) : updater)
  );
}

export async function setAllSlaughterPlans(
  updater:
    | SlaughterPlanRecord[]
    | ((prev: SlaughterPlanRecord[]) => SlaughterPlanRecord[])
) {
  return setAllSlaughterPlan(updater);
}

export async function setAllSlaughterPlanItems(
  updater:
    | SlaughterPlanRecord[]
    | ((prev: SlaughterPlanRecord[]) => SlaughterPlanRecord[])
) {
  return setAllSlaughterPlan(updater);
}

export async function saveSlaughterPlanRecords(records: SlaughterPlanRecord[]) {
  return store.setState(normalizeRecords(records));
}

export function subscribeSlaughterPlan(listener: () => void) {
  return store.subscribe(listener);
}

export function subscribeSlaughterPlans(listener: () => void) {
  return store.subscribe(listener);
}

export function subscribeSlaughterPlanItems(listener: () => void) {
  return store.subscribe(listener);
}

export function subscribeSlaughterPlanRecords(listener: () => void) {
  return store.subscribe(listener);
}

export async function resetSlaughterPlanStore() {
  return store.reset();
}

export function reconcileSlaughterPlanRecords(
  records: SlaughterPlanRecord[],
  animalsOrIds?: number[] | Array<{ id: number }>
) {
  const normalized = normalizeRecords(records);

  if (!Array.isArray(animalsOrIds) || animalsOrIds.length === 0) {
    return normalized;
  }

  const allowedIds = new Set(
    animalsOrIds
      .map((item) =>
        typeof item === "number" ? item : Number(item?.id ?? 0)
      )
      .filter((id) => Number.isFinite(id) && id > 0)
  );

  return normalized.filter((item) => allowedIds.has(item.animalId));
}